import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Timer, TrendingUp } from "lucide-react";
import api from "@/lib/api";

const day = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
const mins = (s) => (s != null ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : "");

function Attempt({ a, testId }) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-0.5 py-1.5 border-b border-slate-100 last:border-0 text-sm" data-testid={testId}>
      <span className="text-slate-500 w-14">{day(a.at)}</span>
      <span className="flex-1 min-w-[110px] font-semibold">{a.title} <span className="font-normal text-slate-500">· {a.subject}</span></span>
      {a.timed && <span className="text-xs text-blue-700 inline-flex items-center gap-1"><Timer size={12} /> {mins(a.seconds)}</span>}
      <span className={`font-bold ${a.pct >= 80 ? "text-green-700" : a.pct >= 50 ? "text-amber-700" : "text-red-600"}`}>{a.score}/{a.total} · {a.pct}%</span>
    </li>
  );
}

function ChildHistory({ c, compact }) {
  const [all, setAll] = useState(false);
  const recent = [...c.attempts].reverse();
  const list = compact ? recent.slice(0, 3) : all ? recent : recent.slice(0, 20);
  const data = c.attempts.map((a, i) => ({ i: i + 1, pct: a.pct, label: `${day(a.at)} · ${a.title}` }));
  return (
    <div className="rounded-2xl border-2 border-slate-200 bg-white p-4" data-testid={`score-child-${c.id}`}>
      <p className="font-bold">{c.name} <span className="font-normal text-slate-500 text-sm">· Grade {c.grade} · {c.attempts.length} {c.attempts.length > 1 ? "essais · tries" : "essai · try"}</span></p>
      {data.length > 1 && (
        <div className="h-28 mt-2" data-testid={`score-chart-${c.id}`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: -28 }}>
              <XAxis dataKey="i" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 100]} ticks={[0, 50, 100]} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => [`${v}%`, "Score"]} labelFormatter={(_, p) => p?.[0]?.payload?.label || ""} />
              <Line type="monotone" dataKey="pct" stroke="#2563EB" strokeWidth={3} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <ul className="mt-2">{list.map((a, i) => <Attempt key={`${a.at}-${i}`} a={a} testId={`score-attempt-${c.id}-${i}`} />)}</ul>
      {!compact && !all && recent.length > 20 && (
        <button onClick={() => setAll(true)} className="mt-2 text-sm underline font-semibold" data-testid={`score-more-${c.id}`}>Voir tout · Show all ({recent.length})</button>
      )}
    </div>
  );
}

// Online test-paper scores over time, per child (parent dashboard: compact, test papers page: full)
export default function TestScoreHistory({ compact = false }) {
  const [kids, setKids] = useState(null);
  useEffect(() => { api.get("/test-papers/history").then(({ data }) => setKids(data.children)).catch(() => setKids([])); }, []);
  const withScores = (kids || []).filter((c) => c.attempts.length);
  if (!withScores.length) return compact ? null : (kids && <p className="text-sm text-slate-500" data-testid="score-history-empty">Pas encore de sujet fait en ligne · No online test done yet.</p>);
  return (
    <section className={compact ? "mb-6" : ""} data-testid="score-history">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp size={18} className="text-blue-600" />
        <h2 className="font-bold text-lg">Notes des sujets en ligne · Test scores</h2>
        {compact && <Link to="/parent/test-papers" className="ml-auto text-sm underline font-semibold" data-testid="score-all-link">Tout voir · See all</Link>}
      </div>
      <div className="grid gap-4 md:grid-cols-2">{withScores.map((c) => <ChildHistory key={c.id} c={c} compact={compact} />)}</div>
    </section>
  );
}
